/**
 * The user-configurable base theme (REV10 — docs/specs/REV10-themes.md), mirroring
 * `src/lib/accent.ts`'s pattern exactly.
 *
 * A theme is a `data-theme="<slug>"` attribute on `<html>` that swaps ONLY the surface token set
 * declared per-theme in `index.css` (`--bg`, `--bg-elevated`, `--bg-elevated-hover`, `--border`,
 * `--border-strong`, `--text`, `--text-muted`, `--text-faint`, `color-scheme`, and — for Maple
 * Day — the fixed semantic colours and `--accent-strong`/`--shell-glow`). The accent system is
 * untouched by theme choice and composes with every theme.
 *
 * `null` means "Maple Night", the default: no `data-theme` attribute renders exactly what
 * `:root` in `index.css` already declares, so a user who never opens the theme picker sees zero
 * visual change.
 *
 * Persistence is localStorage-only, same as the accent (server-side persistence is a later
 * nicety); `applyStoredTheme` re-applies it at boot, before the first paint, so a reload never
 * flashes Maple Night for someone who picked something else.
 */

export type ThemeSlug =
  | 'slate'
  | 'void'
  | 'forest'
  | 'day'
  | 'abyss'
  | 'violet'
  | 'rose'
  | 'ember'
  | 'lagoon'
  | 'indigo'
  | 'olive'
  | 'plum'
  | 'sunset'
  | 'sakura'
  | 'mint'
  | 'sky';

const VALID_SLUGS: ReadonlySet<string> = new Set<ThemeSlug>([
  'slate',
  'void',
  'forest',
  'day',
  'abyss',
  'violet',
  'rose',
  'ember',
  'lagoon',
  'indigo',
  'olive',
  'plum',
  'sunset',
  'sakura',
  'mint',
  'sky',
]);

/**
 * The seventeen theme cards offered on /settings (REV10's original five, plus REV11's twelve --
 * docs/specs/REV11-more-themes.md). `swatch` mirrors the corresponding `index.css`
 * `:root[data-theme='<slug>']` block's `--bg`/`--bg-elevated`/`--text` values, so the mini preview
 * can render a theme's colours without applying it to `<html>` — kept honest by
 * `theme.contrast.test.ts`, which parses `index.css` and asserts these literals match for every
 * preset automatically (no per-theme test to add when a new one lands).
 */
export const THEME_PRESETS: ReadonlyArray<{
  slug: ThemeSlug | null;
  name: string;
  swatch: { bg: string; elevated: string; text: string };
}> = [
  { slug: null, name: 'Maple Night', swatch: { bg: '#0e0b08', elevated: '#171209', text: '#f2ece1' } },
  { slug: 'slate', name: 'Deep Slate', swatch: { bg: '#0c1016', elevated: '#141a23', text: '#eef1f5' } },
  { slug: 'void', name: 'Void', swatch: { bg: '#0a0a0a', elevated: '#141414', text: '#f2f2f2' } },
  { slug: 'forest', name: 'Forest Night', swatch: { bg: '#0a130e', elevated: '#101d15', text: '#e9f2ec' } },
  { slug: 'abyss', name: 'Abyss', swatch: { bg: '#030b1a', elevated: '#081527', text: '#e4edf9' } },
  { slug: 'violet', name: 'Royal Violet', swatch: { bg: '#120a1f', elevated: '#1c1230', text: '#f0e9fb' } },
  { slug: 'rose', name: 'Rose Night', swatch: { bg: '#170a11', elevated: '#23111b', text: '#f7e9ee' } },
  { slug: 'ember', name: 'Ember', swatch: { bg: '#170a09', elevated: '#241210', text: '#f5e6e1' } },
  { slug: 'lagoon', name: 'Lagoon', swatch: { bg: '#061513', elevated: '#0c211d', text: '#e2f5f0' } },
  { slug: 'indigo', name: 'Indigo Dusk', swatch: { bg: '#0b0a24', elevated: '#141138', text: '#eae8fb' } },
  { slug: 'olive', name: 'Olive Grove', swatch: { bg: '#10120a', elevated: '#1a1d10', text: '#eef0e0' } },
  { slug: 'plum', name: 'Plum', swatch: { bg: '#150a14', elevated: '#211320', text: '#f5e7f2' } },
  { slug: 'sunset', name: 'Sunset', swatch: { bg: '#180904', elevated: '#271208', text: '#f9e6d6' } },
  { slug: 'day', name: 'Maple Day', swatch: { bg: '#f7f1e4', elevated: '#fdf9ef', text: '#2a2013' } },
  { slug: 'sakura', name: 'Sakura Day', swatch: { bg: '#f7eef0', elevated: '#fbf4f6', text: '#2b1219' } },
  { slug: 'mint', name: 'Mint Day', swatch: { bg: '#eef6f2', elevated: '#f4fbf7', text: '#132a1f' } },
  { slug: 'sky', name: 'Sky Day', swatch: { bg: '#eef3f7', elevated: '#f4f8fb', text: '#12212b' } },
];

const STORAGE_KEY = 'hypermaple.theme';

function isThemeSlug(value: string): value is ThemeSlug {
  return VALID_SLUGS.has(value);
}

/** Applies a theme to `<html>` and remembers it for the next load. `null` resets to Maple Night. */
export function applyTheme(slug: ThemeSlug | null): void {
  const root = document.documentElement;

  if (slug != null && isThemeSlug(slug)) {
    root.setAttribute('data-theme', slug);
    try {
      localStorage.setItem(STORAGE_KEY, slug);
    } catch {
      // Storage can be unavailable (private mode) -- the theme still applies for this page load.
    }
  } else {
    root.removeAttribute('data-theme');
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Ignore, same as above.
    }
  }
}

/** Reads back whatever theme is currently applied (falls back to Maple Night, i.e. `null`). */
export function currentTheme(): ThemeSlug | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored != null && isThemeSlug(stored)) {
      return stored;
    }
  } catch {
    // Ignore.
  }
  return null;
}

/** Re-applies the theme remembered from the previous session. Called once at startup, before render. */
export function applyStoredTheme(): void {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(STORAGE_KEY);
  } catch {
    return;
  }
  if (stored != null && isThemeSlug(stored)) {
    applyTheme(stored);
  }
  // An invalid/unknown stored slug is silently ignored -- <html> is left with no data-theme
  // attribute, i.e. Maple Night, rather than throwing or applying a partial theme.
}
